import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack, TextField, Button,
} from '@mui/material';
import { Add as AddIcon, Download as DownloadIcon } from '@mui/icons-material';

import { Container, DataGrid } from '@/components/common';
import { useLibraryPosts, useDeleteLibraryPost, useDownloadLibraryFile } from '@/hooks/boards.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { DateCell } from '@/components/pages/components';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { useListViewSessionPersistence } from '@/util/useListViewSessionPersistence';

const LIBRARY_DEFAULT_PAGINATION = { page: 0, pageSize: 10 };
const LIBRARY_DEFAULT_FILTERS = { searchKeyword: '' };

/**
 * 자료실 관리 페이지
 */
const LibraryPage = () => {
  const navigate = useNavigate();

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetListSession,
  } = useListViewSessionPersistence(
    LIST_VIEW_SESSION_KEYS.library,
    LIBRARY_DEFAULT_PAGINATION,
    LIBRARY_DEFAULT_FILTERS
  );

  const searchKeyword = filters.searchKeyword ?? '';
  const setSearchKeyword = useCallback((v) => {
    setFilters((f) => ({ ...f, searchKeyword: typeof v === 'function' ? v(f.searchKeyword) : v }));
  }, [setFilters]);

  const queryParams = useMemo(() => ({
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    ...(searchKeyword && { keyword: searchKeyword }),
  }), [paginationModel, searchKeyword]);

  const { data, isLoading } = useLibraryPosts(queryParams);
  const deleteLibraryMutation = useDeleteLibraryPost();
  const downloadLibraryMutation = useDownloadLibraryFile();

  const total = data?.total ?? data?.data?.total ?? 0;
  const rowData = data?.list ?? data?.data?.list ?? [];

  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) setRowCountState(total);
  }, [total, isLoading]);

  const handleSearch = useCallback(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleReset = useCallback(() => {
    resetListSession();
  }, [resetListSession]);

  const handleRowClick = useCallback((params) => {
    navigate(`/community/library/detail/${params.row.id ?? params.row.libraryId}`);
  }, [navigate]);

  const handleDelete = useCallback((params, e) => {
    e?.stopPropagation();
    const id = params.row.id ?? params.row.libraryId;
    const title = params.row.title ?? params.row.name ?? '-';
    confirmToast(`"${title}" 자료를 삭제하시겠습니까?`, async () => {
      try {
        await deleteLibraryMutation.mutateAsync(id);
        successToast('자료가 삭제되었습니다.');
      } catch (err) {
        errorToast(`자료 삭제에 실패했습니다. - ${err.response?.data?.msg}`);
      }
    });
  }, [deleteLibraryMutation]);

  const handleDownload = useCallback(async (postId, e) => {
    e?.stopPropagation();
    try {
      const res = await downloadLibraryMutation.mutateAsync(postId);
      const blob = res.data;
      const contentDisposition = res.headers?.['content-disposition'];
      let fileName = 'download';
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]*)/);

        if (filenameStarMatch && filenameStarMatch[1]) {
          fileName = decodeURIComponent(filenameStarMatch[1]);
        }
        else {
          const filenameMatch = contentDisposition.match(/filename="?([^";]*)"?/);
          if (filenameMatch && filenameMatch[1]) {
            fileName = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
      successToast('파일이 다운로드되었습니다.');
    } catch (err) {
      errorToast(`파일 다운로드에 실패했습니다. - ${err.response?.data?.msg}`);
    }
  }, [downloadLibraryMutation]);

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.3,
      minWidth: 80,
      sortable: false,
    },
    {
      field: 'title',
      headerName: '제목',
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'categoryName',
      headerName: '카테고리',
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      valueGetter: (value, row) => {
        if (!row) return '-';
        const type = row.category?.categoryType ?? row.categoryType ?? '';
        const name = row.category?.categoryName ?? row.categoryName ?? '';
        return [type, name].filter(Boolean).join(' - ') || '-';
      },
    },
    {
      field: 'createdAt',
      headerName: '등록일',
      flex: 0.3,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <DateCell row={params.row} dateField="createdAt" />
      ),
    },
    {
      field: 'download',
      headerName: '다운로드',
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const hasFile = params.row.hasFile ?? !!params.row.file;
        const postId = params.row.id ?? params.row.libraryId;
        if (!hasFile) return '-';
        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={(e) => handleDownload(postId, e)}
            disabled={downloadLibraryMutation.isPending}
          >
            다운로드
          </Button>
        );
      },
    },
    {
      field: 'actions',
      headerName: '작업',
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={(e) => handleDelete(params, e)}
        >
          삭제
        </Button>
      ),
    },
  ], [handleDelete, handleDownload, downloadLibraryMutation.isPending]);

  return (
    <Container title="자료실 관리">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="제목 검색"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 200 }}
          />
          <Button variant="outlined" onClick={handleSearch} sx={{ height: 40 }}>
            검색
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ height: 40 }}>
            초기화
          </Button>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/community/library/new')}
          sx={{ height: 40 }}
        >
          자료 등록
        </Button>
      </Stack>

      <DataGrid
        height={'auto'}
        rows={rowData}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row, index) => row.id ?? row.libraryId ?? `row-${index}`}
        onRowClick={handleRowClick}
      />
    </Container>
  );
};

export default LibraryPage;
